import { useWorkspaceText } from './interface-copy';
// Meeting notes: the notes banner and the notes panel. Owner: WP-5 (BUILD 1).
//
// A meeting is ready as soon as its transcript is saved; the notes follow in
// their own lane (S6). The banner under the title reports that lane
// (generating, stale, failed with Retry, current with Regenerate) and never
// the meeting's processing (meeting-status.ts `meetingNotesBanner`). The panel
// renders the stored notes: v2 (C-12) with Decisions, Action items, Open
// questions and Risks, owners and resolved due dates marked inferred, every
// item citing its transcript rows; legacy v1 notes render as before.
// Copy: ns/meetings-notes.ts. Props: meeting-slots.ts.
import { useState, type ReactNode } from 'react';
import { useAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsNotesCopy from '@/i18n/ns/meetings-notes';
import { WorkspaceMarkdown } from './markdown';
import { useWorkspace } from './context';
import { routeFor } from './navigation';
import {
  meetingNotesBanner,
  type MeetingNotesAction,
  type MeetingsNotesCopy,
} from './meeting-status';
import { meetingsApi } from './meetings-api';
import type { MeetingJump, MeetingNotesBannerProps, MeetingNotesPanelProps } from './meeting-slots';
import {
  isMeetingSummaryV2,
  type CompanyMeeting,
  type MeetingActionItemV2,
  type MeetingCitation,
  type MeetingNoteList,
} from './types';

/** "#2, #5": the cited rows, numbered from 1 as the transcript shows them. */
const citedRows = (item: MeetingCitation) =>
  item.segmentIndexes.map((index) => `#${index + 1}`).join(', ');

/** A YYYY-MM-DD due date in the interface language; the raw value if it does not parse. */
function formatDueDate(value: string, locale: string) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
  } catch {
    return value;
  }
}

/** The due line: a date resolved from spoken words is marked inferred from them. */
function dueLine(item: MeetingActionItemV2, copy: MeetingsNotesCopy, locale: string) {
  const date = item.dueDate ? formatDueDate(item.dueDate, locale) : null;
  if (date && item.dueText) return formatCopy(copy.dueInferred, { date, text: item.dueText });
  if (date) return formatCopy(copy.due, { date });
  return item.dueText ? formatCopy(copy.dueText, { text: item.dueText }) : null;
}

function ActionItemMeta({ item, copy }: { item: MeetingActionItemV2; copy: MeetingsNotesCopy }) {
  const locale = useAppLocale();
  const owner = item.assignee ? formatCopy(copy.ownerInferred, { name: item.assignee }) : null;
  const due = dueLine(item, copy, locale);
  if (!owner && !due) return null;
  return (
    <p className="ws-muted ws-meeting-item-meta">
      {owner && (
        <span
          data-participant={item.assigneeParticipantId ?? undefined}
          data-evidence={item.assigneeEvidence ?? undefined}
        >
          {owner}
        </span>
      )}
      {owner && due && ' · '}
      {due && <span>{due}</span>}
    </p>
  );
}

function Citation({
  item,
  jump,
  label,
}: {
  item: MeetingCitation;
  jump: MeetingJump;
  label: string;
}) {
  if (!item.segmentIndexes.length) return null;
  return (
    <button className="ws-citation" onClick={() => jump(item.segmentIndexes[0])}>
      {label}
    </button>
  );
}

function Notes<T extends MeetingCitation>({
  title,
  list,
  items,
  copy,
  jump,
  describe,
}: {
  title: string;
  list: MeetingNoteList;
  items: T[] | undefined;
  copy: MeetingsNotesCopy;
  jump: MeetingJump;
  describe?: (item: T) => ReactNode;
}) {
  if (!items?.length) return null;
  return (
    <section className="ws-meeting-notes" data-list={list}>
      <h2>{title}</h2>
      {items.map((item, index) => (
        <div key={`${list}-${index}`}>
          <WorkspaceMarkdown text={item.text} />
          {describe?.(item)}
          <div className="ws-meeting-item-actions">
            <Citation
              item={item}
              jump={jump}
              label={formatCopy(copy.transcriptRows, { rows: citedRows(item) })}
            />
          </div>
        </div>
      ))}
    </section>
  );
}

const BANNER_CALL: Record<MeetingNotesAction, (id: string) => Promise<unknown>> = {
  retryNotes: meetingsApi.regenerateNotes,
  regenerateNotes: meetingsApi.regenerateNotes,
  retryMeeting: meetingsApi.retry,
};

/** The company's usage credit ran out (#2841): said in words, not only as a code. */
const PAYMENT_REQUIRED = 'meeting_payment_required';

/** Rendered under the title: the notes lane's state, with Retry or Regenerate when it can act. */
export function MeetingNotesBanner({ meeting, onRetry }: MeetingNotesBannerProps) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsNotesCopy);
  const [busy, setBusy] = useState(false);
  const banner = meetingNotesBanner(meeting);
  if (!banner) return null;
  const { message, action } = banner;
  const quiet = action === 'regenerateNotes';
  const failureCode = action === 'retryNotes' ? meeting.notes?.failureCode : null;
  const run = (next: MeetingNotesAction) => {
    setBusy(true);
    void BANNER_CALL[next](meeting.id)
      .then(() => onRetry())
      .catch((failure) =>
        ws.notify(failure instanceof Error ? failure.message : copy.notesRetryFailed),
      )
      .finally(() => setBusy(false));
  };
  return (
    <section
      className={`ws-meeting-notes-banner${quiet ? ' ws-muted' : ' ws-meeting-context'}`}
      role="status"
      aria-live="polite"
      data-notes={message}
    >
      {quiet ? <span>{copy[message]}</span> : <strong>{copy[message]}</strong>}
      {failureCode === PAYMENT_REQUIRED && <span>{copy.paymentRequired}</span>}
      {failureCode && <code>{failureCode}</code>}
      {action && meeting.canEdit !== false && (
        <button
          className={quiet ? 'ws-citation' : 'ws-button primary'}
          disabled={busy}
          onClick={() => run(action)}
        >
          {quiet ? copy.regenerateNotes : copy.retryNotes}
        </button>
      )}
    </section>
  );
}

function Overview({
  meeting,
  copy,
  jump,
}: {
  meeting: CompanyMeeting;
  copy: MeetingsNotesCopy;
  jump: MeetingJump;
}) {
  const summary = meeting.summary;
  if (!summary) return null;
  const overview =
    typeof summary.overview === 'string'
      ? { text: summary.overview, segmentIndexes: [] }
      : summary.overview;
  const coverage = isMeetingSummaryV2(summary)
    ? summary.meta?.transcriptCoverage
    : summary.coverage;
  return (
    <div className="ws-meeting-overview">
      <h2>{copy.overview}</h2>
      <WorkspaceMarkdown text={overview?.text ?? ''} />
      {!!overview?.segmentIndexes.length && (
        <button className="ws-citation" onClick={() => jump(overview.segmentIndexes[0])}>
          {copy.showEvidence}
        </button>
      )}
      {coverage?.partial && (
        <p className="ws-page-note">
          {formatCopy(copy.coveragePartial, {
            included: coverage.includedSegmentCount,
            total: coverage.totalSegmentCount,
          })}
        </p>
      )}
    </div>
  );
}

function KnowledgeSuggestions({
  meeting,
  items,
  copy,
  jump,
}: {
  meeting: CompanyMeeting;
  items: MeetingCitation[];
  copy: MeetingsNotesCopy;
  jump: MeetingJump;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  if (!items.length) return null;
  return (
    <section className="ws-knowledge-suggestions">
      <div className="ws-eyebrow">{copy.suggestionsEyebrow}</div>
      <h2>{copy.suggestionsTitle}</h2>
      <p>{copy.suggestionsBody}</p>
      {items.map((item, index) => (
        <div key={index}>
          <WorkspaceMarkdown text={item.text} />
          {meeting.canEdit !== false ? (
            <button
              className="ws-button"
              onClick={() =>
                ws.send(
                  `${formatCopy(copy.reviewInChatPrompt, { text: item.text })}\n\nFrom the meeting “${meeting.title}” (/${routeFor('meetings', { kind: 'meeting', id: meeting.id })})`,
                )
              }
            >
              {copy.reviewInChat}
            </button>
          ) : (
            <button
              className="ws-button"
              onClick={() => ws.send(`Help me understand this meeting suggestion: “${item.text}”`)}
            >
              {uiText('Ask about this suggestion')}
            </button>
          )}
          <Citation item={item} jump={jump} label={copy.showEvidence} />
        </div>
      ))}
    </section>
  );
}

export function MeetingNotesPanel({ meeting, jump }: MeetingNotesPanelProps) {
  const copy = useNamespaceCopy(meetingsNotesCopy);
  const summary = meeting.summary;
  const v2 = isMeetingSummaryV2(summary) ? summary : null;
  const slots = { copy, jump };
  const describeAction = (item: MeetingActionItemV2) => <ActionItemMeta item={item} copy={copy} />;
  return (
    <>
      <Overview meeting={meeting} copy={copy} jump={jump} />
      {summary && (
        <div className="ws-meeting-note-grid">
          <Notes title={copy.decisions} list="decisions" items={summary.decisions} {...slots} />
          {v2 ? (
            <Notes
              title={copy.actionItems}
              list="actionItems"
              items={v2.actionItems}
              describe={describeAction}
              {...slots}
            />
          ) : (
            <Notes
              title={copy.actionItems}
              list="actionItems"
              items={summary.actionItems}
              {...slots}
            />
          )}
          <Notes
            title={copy.openQuestions}
            list="openQuestions"
            items={summary.openQuestions}
            {...slots}
          />
          <Notes title={copy.risks} list="risks" items={v2?.risks} {...slots} />
        </div>
      )}
      <KnowledgeSuggestions
        meeting={meeting}
        items={summary?.knowledgeSuggestions ?? []}
        copy={copy}
        jump={jump}
      />
      {!!meeting.relatedContext?.length && (
        <section className="ws-meeting-context">
          <h2>{copy.relatedContext}</h2>
          {meeting.relatedContext.map((item, index) => (
            <WorkspaceMarkdown key={index} text={item.text} />
          ))}
        </section>
      )}
    </>
  );
}
